import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Calendar, 
  List, 
  Download,
  RefreshCw,
  Search,
  Clock,
  Trash2,
  Pencil,
  Upload,
  X
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { ScheduleCalendar } from './ScheduleCalendar';
import { ScheduleForm } from './ScheduleForm';
import { scheduleService, Schedule, ScheduleInstance } from '../../services/scheduleService';
import { lessonService } from '../../services/lessonService';
import { Lesson } from '../../types';

interface SchedulePageProps {
  teacherId: string;
}

export const SchedulePage: React.FC<SchedulePageProps> = ({ teacherId }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [lessons, setLessons] = useState<Map<string, Lesson>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isImporting, setIsImporting] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState<{
    valid: any[];
    invalid: any[];
  }>({ valid: [], invalid: [] });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [fetchedSchedules, fetchedLessons] = await Promise.all([
        scheduleService.getSchedulesByTeacher(teacherId),
        lessonService.getAllLessons()
      ]);

      setSchedules(fetchedSchedules);
      
      // Create lesson map
      const lessonMap = new Map<string, Lesson>();
      fetchedLessons.forEach(lesson => {
        if (lesson.metadata?.lesson_id) {
          lessonMap.set(lesson.metadata.lesson_id, lesson);
        }
      });
      setLessons(lessonMap);
    } catch (error) {
      console.error('Error loading schedule data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [teacherId]);

  // Load data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle schedule creation/editing
  const handleSaveSchedule = (schedule: Schedule) => {
    if (editingSchedule) {
      setSchedules(prev => prev.map(s => s.id === schedule.id ? schedule : s));
    } else {
      setSchedules(prev => [schedule, ...prev]);
    }
    setShowForm(false);
    setEditingSchedule(null);
  };

  // Handle schedule deletion
  const handleDeleteSchedule = async (scheduleId: string) => {
    if (window.confirm(t('schedule.confirmDelete'))) {
      try {
        await scheduleService.deleteSchedule(scheduleId);
        setSchedules(prev => prev.filter(s => s.id !== scheduleId));
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  // Handle schedule editing
  const handleEditSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setShowForm(true);
  };

  // Handle schedule click
  const handleScheduleClick = (instance: ScheduleInstance) => {
    // Navigate to lesson or show details
    console.log('Schedule clicked:', instance);
  };

  // Export schedule data
  const handleExport = async () => {
    try {
      const data = await scheduleService.exportScheduleData(teacherId, 'json');
      const blob = new Blob([data], { type: 'text/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schedule-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting schedule data:', error);
    }
  };

  // Import schedule data
  const handleImportSchedules = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      console.log(text);
      const importedSchedules = JSON.parse(text);

      if (!Array.isArray(importedSchedules)) {
        throw new Error('Invalid file format. Expected an array of schedules.');
      }

      // Process and validate schedules for preview
      const validSchedules = [];
      const invalidSchedules = [];

      for (const scheduleData of importedSchedules) {
        // Validate required fields according to Schedule interface
        if (!scheduleData.title || !scheduleData.lessonId || !scheduleData.startDate || !scheduleData.startTime || !scheduleData.duration || !scheduleData.recurrence) {
          invalidSchedules.push({
            ...scheduleData,
            error: 'Missing required fields'
          });
          continue;
        }

        // Create schedule preview with teacher ID using exact Schedule interface structure
        const schedulePreview = {
          title: scheduleData.title,
          description: scheduleData.description,
          lessonId: scheduleData.lessonId,
          teacherId: teacherId,
          classId: scheduleData.classId,
          startDate: new Date(scheduleData.startDate),
          endDate: scheduleData.endDate ? new Date(scheduleData.endDate) : undefined,
          startTime: scheduleData.startTime, // Should be "HH:MM" format
          duration: scheduleData.duration, // in minutes
          recurrence: {
            type: scheduleData.recurrence.type,
            interval: scheduleData.recurrence.interval,
            daysOfWeek: scheduleData.recurrence.daysOfWeek,
            dayOfMonth: scheduleData.recurrence.dayOfMonth,
            endAfterOccurrences: scheduleData.recurrence.endAfterOccurrences,
            endDate: scheduleData.recurrence.endDate ? new Date(scheduleData.recurrence.endDate) : undefined
          },
          isActive: scheduleData.isActive !== undefined ? scheduleData.isActive : true,
          maxStudents: scheduleData.maxStudents,
          location: scheduleData.location,
          notes: scheduleData.notes
        };

        validSchedules.push(schedulePreview);
      }

      // Show preview with valid and invalid schedules
      setImportPreviewData({
        valid: validSchedules,
        invalid: invalidSchedules
      });
      setShowImportPreview(true);
    } catch (error) {
      console.error('Error processing import file:', error);
      alert(t('schedule.importError'));
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Confirm and create schedules after preview
  const handleConfirmImport = async () => {
    setIsImporting(true);
    try {
      // Create each valid schedule
      for (const scheduleData of importPreviewData.valid) {
        await scheduleService.createSchedule(scheduleData);
      }

      // Reload data after import
      await loadData();
      
      // Show success message
      alert(t('schedule.importSuccess', { count: importPreviewData.valid.length }));
      
      // Close preview
      setShowImportPreview(false);
      setImportPreviewData({ valid: [], invalid: [] });
    } catch (error) {
      console.error('Error creating schedules:', error);
      alert(t('schedule.importError'));
    } finally {
      setIsImporting(false);
    }
  };

  // Cancel import preview
  const handleCancelImport = () => {
    setShowImportPreview(false);
    setImportPreviewData({ valid: [], invalid: [] });
  };

  // Filter schedules
  const filteredSchedules = schedules.filter(schedule => {
    const matchesSearch = schedule.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         schedule.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && schedule.isActive) ||
                         (filterStatus === 'inactive' && !schedule.isActive);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('schedule.scheduleManagement')}</h1>
          <p className="text-gray-600 mt-1">{t('schedule.manageLessonSchedules')}</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('schedule.createSchedule')}</span>
          </button>
          
          {/* Import Button */}
          <div className="relative">
            <input
              type="file"
              accept=".json"
              onChange={handleImportSchedules}
              className="hidden"
              id="schedule-import"
              disabled={isImporting}
            />
            <label
              htmlFor="schedule-import"
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                isImporting
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <Upload className="w-4 h-4" />
              <span>{isImporting ? t('schedule.importing') : t('schedule.import')}</span>
            </label>
          </div>
          
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>{t('schedule.export')}</span>
          </button>
        </div>
      </div>

      {/* View Toggle and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              viewMode === 'calendar'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>{t('schedule.calendar')}</span>
          </button>
          
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <List className="w-4 h-4" />
            <span>{t('schedule.list')}</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('schedule.searchSchedules')}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">{t('schedule.allSchedules')}</option>
            <option value="active">{t('schedule.activeSchedules')}</option>
            <option value="inactive">{t('schedule.inactiveSchedules')}</option>
          </select>
          
          <button
            onClick={loadData}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('schedule.refresh')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {viewMode === 'calendar' ? (
          <motion.div
            key="calendar"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ScheduleCalendar
              teacherId={teacherId}
              onScheduleClick={handleScheduleClick}
              onEditSchedule={handleEditSchedule}
              onDeleteSchedule={handleDeleteSchedule}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('schedule.scheduleList')} ({filteredSchedules.length})
                </h3>
              </div>
              
              <div className="divide-y divide-gray-200">
                {isLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">{t('schedule.loading')}</p>
                  </div>
                ) : filteredSchedules.length === 0 ? (
                  <div className="p-8 text-center text-gray-600">
                    {searchTerm || filterStatus !== 'all' 
                      ? t('schedule.noSchedulesFound') 
                      : t('schedule.noSchedulesCreated')}
                  </div>
                ) : (
                  filteredSchedules.map((schedule) => {
                    const lesson = lessons.get(schedule.lessonId);
                    
                    return (
                      <div key={schedule.id} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              <h4 className="text-lg font-medium text-gray-900 truncate">
                                {schedule.title}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                schedule.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {schedule.isActive ? t('schedule.active') : t('schedule.inactive')}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 mt-1">
                              {schedule.description || t('schedule.noDescription')}
                            </p>
                            
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>{schedule.startDate.toLocaleDateString()}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{schedule.startTime} ({schedule.duration} min)</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <List className="w-4 h-4" />
                                <span>{t(`schedule.recurrence.${schedule.recurrence.type}`)}</span>
                              </span>
                            </div>
                            
                            {lesson && (
                              <p className="text-sm text-gray-500 mt-1">
                                {t('schedule.lesson')}: {lesson.metadata?.title}
                {lesson.className && ` (${lesson.className})`}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleEditSchedule(schedule)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title={t('schedule.edit')}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id!)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title={t('schedule.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schedule Form Modal */}
      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <ScheduleForm
                teacherId={teacherId}
                schedule={editingSchedule || undefined}
                onSave={handleSaveSchedule}
                onCancel={() => {
                  setShowForm(false);
                  setEditingSchedule(null);
                }}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Import Preview Modal */}
      <AnimatePresence>
        {showImportPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-6xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-xl"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">{t('schedule.importPreview')}</h2>
                  <button
                    onClick={handleCancelImport}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">
                  {t('schedule.importPreviewDescription', { 
                    valid: importPreviewData.valid.length, 
                    invalid: importPreviewData.invalid.length 
                  })}
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Valid Schedules */}
                {importPreviewData.valid.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-green-700 mb-4">
                      {t('schedule.validSchedules')} ({importPreviewData.valid.length})
                    </h3>
                    <div className="space-y-3">
                      {importPreviewData.valid.map((schedule, index) => (
                        <div key={index} className="p-4 border border-green-200 rounded-lg bg-green-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-green-900">{schedule.title}</h4>
                              <p className="text-sm text-green-700 mt-1">
                                {schedule.description || t('schedule.noDescription')}
                              </p>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-green-600">
                                <span>{schedule.startDate.toLocaleDateString()}</span>
                                <span>{schedule.startTime} ({schedule.duration} min)</span>
                                <span>{t(`schedule.recurrence.${schedule.recurrence.type}`)}</span>
                                {schedule.location && <span>{schedule.location}</span>}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                {t('schedule.valid')}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invalid Schedules */}
                {importPreviewData.invalid.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-red-700 mb-4">
                      {t('schedule.invalidSchedules')} ({importPreviewData.invalid.length})
                    </h3>
                    <div className="space-y-3">
                      {importPreviewData.invalid.map((schedule, index) => (
                        <div key={index} className="p-4 border border-red-200 rounded-lg bg-red-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-red-900">
                                {schedule.title || t('schedule.untitled')}
                              </h4>
                              <p className="text-sm text-red-700 mt-1">
                                {schedule.error || t('schedule.validationError')}
                              </p>
                              {schedule.description && (
                                <p className="text-sm text-red-600 mt-1">{schedule.description}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                {t('schedule.invalid')}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={handleCancelImport}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('schedule.cancel')}
                  </button>
                  <button
                    onClick={handleConfirmImport}
                    disabled={isImporting || importPreviewData.valid.length === 0}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isImporting || importPreviewData.valid.length === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {isImporting ? t('schedule.importing') : t('schedule.confirmImport')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
